import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, Clock, XCircle, BadgeCheck, CreditCard, Upload, X, Handshake, AlertTriangle } from "lucide-react";
import api, { formatRON, formatApiError, mediaUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";
import { LANGUAGES, DELIVERY_CHANNELS, optLabel } from "@/lib/serviceFields";
import { toast } from "sonner";

const emptyForm = {
  name: "", company: "", photo: "", location: "", specializations: [], years_experience: "",
  certifications: "", system_competencies: "", availability: [], bio: "", projects: "", contact_email: "", contact_phone: "", website: "",
  languages: [], delivery_channels: [], cov_base_city: "", cov_radius_km: "", cov_national: false, cov_international: false,
};

const statusBanner = {
  pending: { icon: Clock, cls: "bg-amber-500/10 border-amber-500/40 text-amber-300", label: "În verificare", text: "Profilul tău este în curs de verificare de către echipa OXO." },
  verified: { icon: CheckCircle2, cls: "bg-green-500/10 border-green-500/40 text-green-300", label: "Verificat", text: "Profil verificat. Activează-l prin plata abonamentului anual pentru a apărea în director." },
  rejected: { icon: XCircle, cls: "bg-red-500/10 border-red-500/40 text-red-300", label: "Respins", text: "Profilul a fost respins. Editează și retrimite." },
};

export default function ExpertProfile() {
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  const [profile, setProfile] = useState(undefined); // undefined=loading, null=none, obj=exists
  const [specs, setSpecs] = useState([]);
  const [availOptions, setAvailOptions] = useState([]);
  const [opts, setOpts] = useState({ fee_standard: 1200, fee_senior: 2400, senior_threshold_years: 15 });
  const [form, setForm] = useState(emptyForm);
  const { t, i18n } = useTranslation();
  const lng = (i18n.language || "ro").slice(0, 2);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(false);
  const [consultations, setConsultations] = useState([]);
  const polled = useRef(false);

  const loadProfile = useCallback(() =>
    api.get("/my-expert-profile").then((r) => {
      setProfile(r.data);
      if (r.data) setForm({ ...emptyForm, ...r.data, years_experience: r.data.years_experience ?? "", photo: r.data.photo || "",
        languages: r.data.languages || [], delivery_channels: r.data.delivery_channels || [],
        cov_base_city: r.data.coverage?.base_city || "", cov_radius_km: r.data.coverage?.radius_km ?? "",
        cov_national: !!r.data.coverage?.national, cov_international: !!r.data.coverage?.international });
    }).catch(() => setProfile(null)), []);

  useEffect(() => {
    api.get("/experts/options").then((r) => {
      setSpecs(r.data.specializations || []);
      setAvailOptions(r.data.availability || []);
      setOpts(r.data);
    }).catch(() => {});
    loadProfile();
    api.get("/my-consultations").then((r) => setConsultations(r.data)).catch(() => {});
  }, [loadProfile]);

  useEffect(() => {
    if (!sessionId || polled.current) return;
    polled.current = true;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Abonament activat! Profilul tău este acum public.");
          setParams({});
          loadProfile();
          return;
        }
        if (data.status === "expired" || attempts >= 6) { toast.error("Plata nu a fost confirmată."); setParams({}); return; }
        setTimeout(poll, 2000);
      } catch { setParams({}); }
    };
    poll();
  }, [sessionId, setParams, loadProfile]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleSpec = (s) => setForm((f) => ({ ...f, specializations: f.specializations.includes(s) ? f.specializations.filter((x) => x !== s) : [...f.specializations, s] }));
  const toggleAvail = (a) => setForm((f) => ({ ...f, availability: f.availability.includes(a) ? f.availability.filter((x) => x !== a) : [...f.availability, a] }));
  const toggleArr = (k, v) => setForm((f) => ({ ...f, [k]: (f[k] || []).includes(v) ? f[k].filter((x) => x !== v) : [...(f[k] || []), v] }));
  const isSenior = form.years_experience !== "" && Number(form.years_experience) >= opts.senior_threshold_years;
  const tierFee = isSenior ? opts.fee_senior : opts.fee_standard;

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/experts/upload-photo", fd);
      setForm((f) => ({ ...f, photo: r.data.path }));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Eroare la încărcare"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Adaugă numele");
    if (!form.contact_email.trim()) return toast.error("Adaugă un email de contact");
    if (!form.specializations.length) return toast.error("Alege cel puțin o specializare");
    setSaving(true);
    try {
      await api.post("/experts", {
        ...form, years_experience: form.years_experience === "" ? null : Number(form.years_experience),
        coverage: {
          base_city: form.cov_base_city || null,
          radius_km: form.cov_radius_km === "" ? null : Number(form.cov_radius_km),
          national: !!form.cov_national, international: !!form.cov_international,
        },
      });
      toast.success("Profil trimis spre verificare.");
      loadProfile();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/experts/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setPaying(false); }
  };

  if (profile === undefined) return <div className="bg-slate-950 text-white min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;

  const banner = profile ? statusBanner[profile.status] : null;

  return (
    <div className="bg-slate-950 text-white min-h-screen" data-testid="expert-profile-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <span className="text-xs uppercase tracking-[0.3em] font-bold text-orange-500">Profil expert</span>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter mt-3">{profile ? "Gestionează profilul tău" : "Devino expert OXO"}</h1>
        <p className="text-slate-400 mt-3">Listare premium în Directorul de experți B2B. Abonament anual de la {formatRON(opts.fee_standard)}.</p>
        <div className="border border-orange-900/50 bg-orange-950/20 px-5 py-4 mt-5 text-sm text-slate-300" data-testid="tier-note">
          <span className="font-bold text-orange-400">„Cine nu are oameni experimentați, să-și cumpere.”</span> Specialiștii cu peste {opts.senior_threshold_years} ani de experiență primesc insigna <span className="font-bold text-white">Veteran</span>, apar în vârful rezultatelor și beneficiază de tariful premium ({formatRON(opts.fee_senior)}/an).
        </div>

        {banner && (
          <div className={`border px-5 py-4 mt-6 flex items-start gap-3 ${banner.cls}`} data-testid="expert-status-banner">
            <banner.icon className="w-5 h-5 mt-0.5 shrink-0" />
            <div>
              <div className="font-bold flex items-center gap-2">{banner.label}{profile.endorsed && <span className="inline-flex items-center gap-1 text-orange-400 text-xs"><BadgeCheck className="w-3.5 h-3.5" /> OXO Endorsed</span>}{profile.is_public && <span className="text-green-400 text-xs">· Public</span>}</div>
              <p className="text-sm opacity-90 mt-0.5">{profile.status === "rejected" && profile.reject_reason ? `Motiv: ${profile.reject_reason}` : banner.text}</p>
            </div>
          </div>
        )}

        {profile && profile.expired && (
          <div className="border border-red-500/40 bg-red-500/10 text-red-300 px-5 py-4 mt-4 flex items-start gap-3" data-testid="expired-banner">
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
            <div>
              <div className="font-bold">Abonament expirat</div>
              <p className="text-sm opacity-90 mt-0.5">Profilul tău a fost ascuns din director. Reînnoiește abonamentul pentru a reapărea în rezultate.</p>
            </div>
          </div>
        )}

        {profile && profile.status === "verified" && (!profile.active || profile.expired) && (
          <button onClick={pay} disabled={paying} className="w-full mt-5 inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white px-6 py-4 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="pay-subscription">
            <CreditCard className="w-4 h-4" /> {paying ? "Se redirecționează..." : `${profile.expired ? "Reînnoiește abonamentul" : "Activează abonamentul"} · ${formatRON(profile.annual_fee || tierFee)}/an${profile.senior ? " (Veteran)" : ""}`}
          </button>
        )}

        {profile && profile.is_public && profile.paid_until && (
          <p className="text-xs text-slate-500 mt-2" data-testid="paid-until">Abonament activ până la {new Date(profile.paid_until).toLocaleDateString("ro-RO")}.</p>
        )}

        {consultations.length > 0 && (
          <div className="border border-slate-800 mt-6" data-testid="my-consultations">
            <div className="bg-slate-900 px-5 py-3 text-xs uppercase tracking-wide font-bold text-slate-400 flex items-center gap-2"><Handshake className="w-4 h-4 text-orange-500" /> Cereri de consultanță primite ({consultations.length})</div>
            <div className="divide-y divide-slate-800">
              {consultations.map((c) => (
                <div key={c.id} className="px-5 py-4" data-testid={`consultation-${c.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="font-semibold">{c.company} <span className="text-slate-400 font-normal">· {c.contact_name}</span></div>
                    <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 ${c.urgency === "high" ? "bg-red-600 text-white" : c.urgency === "medium" ? "bg-amber-500 text-slate-950" : "bg-slate-700 text-slate-200"}`}>{c.issue_type}</span>
                  </div>
                  <p className="text-sm text-slate-300 mt-2">{c.project_description}</p>
                  <div className="text-xs text-slate-500 mt-2 flex flex-wrap gap-3">
                    <span>{c.contact_email}{c.contact_phone ? ` · ${c.contact_phone}` : ""}</span>
                    {c.estimated_fee ? <span>Buget: {formatRON(c.estimated_fee)}</span> : null}
                    <span className="capitalize">Status: {c.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}


        <div className="space-y-5 mt-8">
          <div className="flex items-center gap-5">
            <div className="w-24 h-24 bg-slate-800 border border-slate-700 overflow-hidden shrink-0">
              {form.photo ? <img src={mediaUrl(form.photo)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-600 text-xs">Foto</div>}
            </div>
            <label className="inline-flex items-center gap-2 border border-slate-700 hover:border-orange-600 px-4 py-2.5 text-sm cursor-pointer transition-colors" data-testid="expert-photo-label">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Încarcă fotografie / logo
              <input type="file" accept="image/*" className="hidden" onChange={onPhoto} disabled={uploading} data-testid="expert-photo-input" />
            </label>
            {form.photo && <button onClick={() => setForm({ ...form, photo: "" })} className="text-slate-400 hover:text-red-400"><X className="w-4 h-4" /></button>}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Nume *</Label><Input value={form.name} onChange={set("name")} className="rounded-none bg-slate-900 border-slate-700 text-white mt-1.5" data-testid="expert-name" /></div>
            <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Companie</Label><Input value={form.company} onChange={set("company")} className="rounded-none bg-slate-900 border-slate-700 text-white mt-1.5" data-testid="expert-company" /></div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Specializări *</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {specs.map((s) => (
                <button key={s} type="button" onClick={() => toggleSpec(s)} className={`px-3 py-1.5 text-xs uppercase tracking-wide font-bold border transition-colors ${form.specializations.includes(s) ? "bg-orange-600 border-orange-600 text-white" : "border-slate-700 text-slate-300 hover:border-slate-500"}`} data-testid={`expert-spec-${s}`}>{s}</button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Localitate</Label><Input value={form.location} onChange={set("location")} className="rounded-none bg-slate-900 border-slate-700 text-white mt-1.5" data-testid="expert-location" /></div>
            <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Ani experiență</Label><Input type="number" value={form.years_experience} onChange={set("years_experience")} className="rounded-none bg-slate-900 border-slate-700 text-white mt-1.5" data-testid="expert-years" />{isSenior && <span className="text-xs text-orange-400 mt-1 inline-block" data-testid="senior-hint">★ Tier Veteran ({opts.senior_threshold_years}+ ani) — {formatRON(opts.fee_senior)}/an</span>}</div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Disponibilitate</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {availOptions.map((a) => (
                <button key={a} type="button" onClick={() => toggleAvail(a)} className={`px-3 py-1.5 text-xs uppercase tracking-wide font-bold border transition-colors ${form.availability.includes(a) ? "bg-orange-600 border-orange-600 text-white" : "border-slate-700 text-slate-300 hover:border-slate-500"}`} data-testid={`expert-avail-${a}`}>{a}</button>
              ))}
            </div>
          </div>

          <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Competențe de sistem (hardware/software)</Label><Textarea value={form.system_competencies} onChange={set("system_competencies")} placeholder="Ex: controlere Logica H&S, motoare CNC Winter, PLC Siemens..." className="rounded-none bg-slate-900 border-slate-700 text-white mt-1.5" data-testid="expert-competencies" /></div>
          <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Certificări</Label><Textarea value={form.certifications} onChange={set("certifications")} placeholder="Ex: ISPM-15, ISO 9001..." className="rounded-none bg-slate-900 border-slate-700 text-white mt-1.5" data-testid="expert-certifications" /></div>
          <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Bio</Label><Textarea value={form.bio} onChange={set("bio")} className="rounded-none bg-slate-900 border-slate-700 text-white mt-1.5 min-h-[110px]" data-testid="expert-bio" /></div>
          <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Proiecte anterioare</Label><Textarea value={form.projects} onChange={set("projects")} className="rounded-none bg-slate-900 border-slate-700 text-white mt-1.5" data-testid="expert-projects" /></div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Email contact *</Label><Input type="email" value={form.contact_email} onChange={set("contact_email")} className="rounded-none bg-slate-900 border-slate-700 text-white mt-1.5" data-testid="expert-email" /></div>
            <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Telefon</Label><Input value={form.contact_phone} onChange={set("contact_phone")} className="rounded-none bg-slate-900 border-slate-700 text-white mt-1.5" data-testid="expert-phone" /></div>
          </div>
          <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Website</Label><Input value={form.website} onChange={set("website")} placeholder="https://..." className="rounded-none bg-slate-900 border-slate-700 text-white mt-1.5" data-testid="expert-website" /></div>

          <div>
            <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">{t("svc.languages")}</Label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="expert-languages">
              {LANGUAGES.map((o) => (
                <button type="button" key={o.value} onClick={() => toggleArr("languages", o.value)}
                  className={`text-xs font-semibold px-3 py-1.5 border transition-colors ${form.languages.includes(o.value) ? "border-orange-500 bg-orange-500 text-white" : "border-slate-600 text-slate-300 hover:border-orange-400"}`}
                  data-testid={`expert-lang-${o.value}`}>{optLabel(o, lng)}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">{t("svc.delivery")}</Label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="expert-delivery">
              {DELIVERY_CHANNELS.map((o) => (
                <button type="button" key={o.value} onClick={() => toggleArr("delivery_channels", o.value)}
                  className={`text-xs font-semibold px-3 py-1.5 border transition-colors ${form.delivery_channels.includes(o.value) ? "border-orange-500 bg-orange-500 text-white" : "border-slate-600 text-slate-300 hover:border-orange-400"}`}
                  data-testid={`expert-delivery-${o.value}`}>{optLabel(o, lng)}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">{t("svc.coverage")}</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <Input value={form.cov_base_city} onChange={set("cov_base_city")} placeholder={t("svc.baseCity")} className="rounded-none bg-slate-900 border-slate-700 text-white" data-testid="expert-cov-city" />
              <Input type="number" value={form.cov_radius_km} onChange={set("cov_radius_km")} placeholder={t("svc.radiusKm")} className="rounded-none bg-slate-900 border-slate-700 text-white" data-testid="expert-cov-radius" />
            </div>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-300">
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.cov_national} onChange={(e) => setForm({ ...form, cov_national: e.target.checked })} data-testid="expert-cov-national" /> {t("svc.national")}</label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.cov_international} onChange={(e) => setForm({ ...form, cov_international: e.target.checked })} data-testid="expert-cov-international" /> {t("svc.international")}</label>
            </div>
          </div>

          <button onClick={submit} disabled={saving || uploading} className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white px-6 py-4 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="submit-expert">
            {saving ? "Se trimite..." : profile ? "Salvează și retrimite spre verificare" : "Trimite profilul spre verificare"}
          </button>
        </div>
      </div>
    </div>
  );
}
